import { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function getToken() {
  try { return localStorage.getItem('auth_token'); } catch { return null; }
}

/**
 * Custom hook for AI chat state management and API calls.
 */
export default function useAIChat(vehicle) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [mode, setMode] = useState('general');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const getAuthHeaders = useCallback(() => {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }, []);

  useEffect(() => {
    const loadSessions = async () => {
      setLoadingSessions(true);
      try {
        const res = await fetch(`${API_URL}/api/ai/sessions`, { headers: getAuthHeaders() });
        if (res.ok) setSessions(await res.json());
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('Load sessions error:', err);
      }
      setLoadingSessions(false);
    };
    loadSessions();
  }, [getAuthHeaders]);

  const loadHistory = useCallback(async (sid) => {
    try {
      const res = await fetch(`${API_URL}/api/ai/history/${sid}`, { headers: getAuthHeaders() });
      if (res.ok) {
        setMessages(await res.json());
        setSessionId(sid);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Load history error:', err);
    }
  }, [getAuthHeaders]);

  const handleNewChat = useCallback(() => {
    setSessionId(null);
    setMessages([]);
    setInput('');
    inputRef.current?.focus();
  }, []);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || sending) return;

    const userMsg = { role: 'user', content: text.trim(), mode, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    let contextPrefix = '';
    if (vehicle && !sessionId) {
      contextPrefix = `[Vehicle Context: ${vehicle.year} ${vehicle.make} ${vehicle.model}, Engine: ${vehicle.engine}, ECU: ${vehicle.ecu_type}${vehicle.mods?.length ? `, Mods: ${vehicle.mods.join(', ')}` : ''}]\n\n`;
    }

    try {
      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ message: contextPrefix + text.trim(), session_id: sessionId, mode }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'AI service error' }));
        throw new Error(err.detail || 'Failed to get response');
      }
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response, mode: data.mode, created_at: new Date().toISOString() }]);

      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        const sessRes = await fetch(`${API_URL}/api/ai/sessions`, { headers: getAuthHeaders() });
        if (sessRes.ok) setSessions(await sessRes.json());
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `**Error:** ${err.message}\n\nMake sure you're logged in and the AI service is configured.`,
        mode: 'general', created_at: new Date().toISOString(),
      }]);
    }
    setSending(false);
  }, [sending, mode, sessionId, vehicle, getAuthHeaders]);

  return {
    messages, input, setInput, mode, setMode, sending, sessionId,
    sessions, loadingSessions, messagesEndRef, inputRef,
    loadHistory, handleNewChat, sendMessage,
  };
}
